import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmtEur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Download, Truck, RefreshCw, ShieldCheck, FileText, ExternalLink, CircleCheck } from "lucide-react";
import AddressAutocomplete from "@/components/app/AddressAutocomplete";

const STATUS_LABELS = {
  draft: "Brouillon",
  pending_review: "En vérification",
  waiting_payment: "En attente de paiement",
  approved_for_payment: "Validé — prêt au paiement",
  missing_information: "Informations complémentaires requises",
  rejected: "Refusé",
  paid: "Payé",
  submitted: "Soumis au prestataire",
  in_transit: "En cours d'acheminement",
  delivered: "Livré",
  closed: "Clôturé",
  cancelled: "Annulé",
};

const STATUS_COLORS = {
  draft: "secondary",
  pending_review: "outline",
  waiting_payment: "outline",
  approved_for_payment: "default",
  missing_information: "outline",
  rejected: "destructive",
  paid: "default",
  in_transit: "outline",
  delivered: "default",
};

const OFFER_LABELS = { download: "Offre sur mesure", assisted: "Envoi assisté", premium: "Accompagnement complet" };

async function downloadAuthed(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error("Téléchargement impossible");
  }
}

export function ClientDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);

  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)); }, []);

  const drafts = cases.filter((c) => c.status === "draft" || c.status === "waiting_payment");
  const active = cases.filter((c) => ["paid", "submitted", "in_transit"].includes(c.status));
  const done = cases.filter((c) => c.status === "delivered" || c.status === "closed");

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Bonjour {user?.first_name || user?.email}</p>
          <h1 className="text-3xl font-bold tracking-tight">Tableau de bord</h1>
        </div>
        <Button asChild className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="new-case-btn">
          <Link to="/resilier"><Plus className="h-4 w-4 mr-1" />Nouvelle résiliation</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Brouillons & en attente" value={drafts.length} />
        <StatCard label="Résiliations en cours" value={active.length} />
        <StatCard label="Terminées" value={done.length} />
      </div>

      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Mes dossiers récents</h2>
        </div>
        {cases.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <p className="mb-4">Aucune résiliation pour le moment.</p>
            <Button asChild><Link to="/resilier">Créer ma première résiliation</Link></Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {cases.slice(0, 8).map((c) => (
              <Link key={c.id} to={`/espace/dossiers/${c.id}`} className="flex items-center justify-between py-3 hover:bg-secondary/40 -mx-3 px-3 rounded transition-colors duration-200" data-testid={`case-item-${c.id}`}>
                <div>
                  <div className="font-medium">{c.organization_name || "Sans organisme"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{new Date(c.created_at).toLocaleDateString("fr-FR")} · {c.id.slice(0, 8)}</div>
                </div>
                <Badge variant={STATUS_COLORS[c.status] || "outline"}>{STATUS_LABELS[c.status] || c.status}</Badge>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="card-elevated p-6">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">{label}</div>
      <div className="text-3xl font-bold font-mono">{value}</div>
    </div>
  );
}

export function CasesListPage() {
  const [cases, setCases] = useState([]);
  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Mes résiliations</h1>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <th className="text-left px-4 py-3">Organisme</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-left px-4 py-3">N° suivi</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{c.organization_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="px-4 py-3"><Badge variant="outline">{STATUS_LABELS[c.status] || c.status}</Badge></td>
                <td className="px-4 py-3 font-mono text-xs">{c.tracking_number || "—"}</td>
                <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" asChild data-testid={`case-view-${c.id}`}><Link to={`/espace/dossiers/${c.id}`}>Voir</Link></Button></td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Aucun dossier.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CaseDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [diag, setDiag] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const refresh = async () => {
    const [ca, sh, ev, dg] = await Promise.all([
      api.get(`/cases/${id}`),
      api.get(`/cases/${id}/shipment`).catch(() => ({ data: null })),
      api.get(`/cases/${id}/evidence`).catch(() => ({ data: null })),
      api.get(`/cases/${id}/diagnostic`).catch(() => ({ data: null })),
    ]);
    setC(ca.data); setShipment(sh.data); setEvidence(ev.data); setDiag(dg.data);
  };
  useEffect(() => { refresh(); }, [id]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post(`/cases/${id}/shipment/sync`);
      await refresh();
      toast.success("Suivi mis à jour");
    } catch { toast.error("Erreur de synchronisation"); }
    finally { setSyncing(false); }
  };

  const startPayment = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/payments/paypal/create-order", { caseId: id });
      if (data?.approveUrl) {
        // Redirect user to PayPal so they can actually approve and pay
        window.location.href = data.approveUrl;
      } else {
        toast.error("Impossible de récupérer l'URL de paiement PayPal. Contactez le support.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'initialisation du paiement");
    } finally { setPayLoading(false); }
  };

  if (!c) return <div>Chargement...</div>;
  const isApprovedForPayment = c.status === "approved_for_payment" || c.status === "waiting_payment";
  const paymentEligible = !!(diag && diag.payment_eligible);
  const reasonNotPayable = diag && !diag.payment_eligible ? diag.reason_not_payable : null;
  const amountTtc = (c.price_cents ?? diag?.amount_cents ?? 0) / 100;

  return (
    <div>
      <Link to="/espace/dossiers" className="text-sm text-muted-foreground hover:text-foreground">← Retour</Link>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight break-words">{c.organization_name || "Sans organisme"}</h1>
          <div className="text-xs sm:text-sm text-muted-foreground font-mono break-all">{c.id}</div>
        </div>
        <Badge variant={STATUS_COLORS[c.status] || "outline"} className="w-fit whitespace-nowrap">{STATUS_LABELS[c.status] || c.status}</Badge>
      </div>

      {isApprovedForPayment && (
        <div className="card-elevated p-5 sm:p-6 mb-6 border-2 border-primary/60 bg-primary/5" data-testid="approved-payment-card">
          <div className="flex items-start gap-3 mb-3">
            <CircleCheck className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold">Votre dossier est validé</h2>
              <p className="text-sm text-muted-foreground mt-1">Votre dossier a été vérifié. Vous pouvez maintenant procéder au paiement.</p>
            </div>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mt-4 mb-5">
            <div><dt className="text-muted-foreground">Offre choisie</dt><dd className="font-medium" data-testid="approved-offer">{OFFER_LABELS[c.offer] || c.offer || "—"}</dd></div>
            <div><dt className="text-muted-foreground">Montant TTC</dt><dd className="font-mono font-semibold" data-testid="approved-amount">{amountTtc.toFixed(2)} €</dd></div>
            <div><dt className="text-muted-foreground">Date de validation</dt><dd data-testid="approved-date">{c.reviewed_at ? new Date(c.reviewed_at).toLocaleDateString("fr-FR") : "—"}</dd></div>
            <div><dt className="text-muted-foreground">Référence</dt><dd className="font-mono text-xs break-all" data-testid="approved-ref">{c.id}</dd></div>
          </dl>
          {paymentEligible ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={startPayment}
                disabled={payLoading}
                className="w-full sm:w-auto bg-gradient-brand text-white border-0 hover:opacity-90"
                data-testid="pay-and-finalize-btn"
              >
                {payLoading ? "Redirection PayPal..." : "Payer et finaliser"}
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto" data-testid="view-case-btn">
                <Link to={`/espace/dossiers/${c.id}`}>Voir mon dossier</Link>
              </Button>
            </div>
          ) : (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900" data-testid="not-payable-reason">
              {reasonNotPayable || "Le paiement n'est pas encore accessible."}
            </div>
          )}
        </div>
      )}

      {c.status === "missing_information" && (
        <div className="card-elevated p-5 sm:p-6 mb-6 border border-yellow-300 bg-yellow-50/70">
          <h2 className="text-lg font-bold text-yellow-900">Informations complémentaires requises</h2>
          <p className="text-sm text-yellow-900/80 mt-2">Merci de reprendre votre dossier pour compléter les éléments demandés.</p>
          <Button onClick={() => nav(`/resilier/${c.id}`)} className="mt-3" data-testid="continue-missing-info-btn">Reprendre le dossier</Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-elevated p-6">
            <h2 className="font-semibold mb-4">Informations</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="text-muted-foreground">Catégorie</dt><dd className="font-medium">{c.category_slug || "—"}</dd></div>
              <div><dt className="text-muted-foreground">Motif</dt><dd className="font-medium">{c.reason || "—"}</dd></div>
              <div><dt className="text-muted-foreground">Offre</dt><dd className="font-medium">{OFFER_LABELS[c.offer] || c.offer || "—"}</dd></div>
              <div><dt className="text-muted-foreground">Montant</dt><dd className="font-mono">{fmtEur(c.price_cents || 0)}</dd></div>
            </dl>
            {c.status === "draft" && (
              <Button onClick={() => nav(`/resilier/${c.id}`)} className="mt-4" data-testid="continue-case-btn">Reprendre le dossier</Button>
            )}
          </div>

          {shipment && (
            <div className="card-elevated p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h2 className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4" />Suivi postal</h2>
                <Button variant="outline" size="sm" onClick={sync} disabled={syncing} data-testid="sync-tracking-btn" className="w-fit">
                  <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? "animate-spin" : ""}`} />Synchroniser
                </Button>
              </div>
              <div className="text-xs text-muted-foreground font-mono mb-4 break-all">Suivi : {shipment.tracking_number || "—"} · Prestataire : {shipment.provider}</div>
              <ol className="space-y-3 border-l-2 border-border ml-2 pl-4">
                {(shipment.events || []).map((e, i) => (
                  <li key={`${e.code || "evt"}-${e.occurred_at || i}`} className="relative">
                    <div className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-primary" />
                    <div className="text-sm font-medium">{e.description}</div>
                    <div className="text-xs text-muted-foreground font-mono">{new Date(e.occurred_at).toLocaleString("fr-FR")}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="card-elevated p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Preuves & documents</h2>
            <div className="space-y-2 text-sm">
              {c.letter_content && (
                <button
                  type="button"
                  onClick={() => downloadAuthed(`/cases/${c.id}/letter/pdf`, `lettre-resiliation-${c.id.slice(0, 8)}.pdf`)}
                  className="w-full flex items-center justify-between border border-border rounded-md p-2 hover:bg-secondary transition-colors duration-200 text-left"
                  data-testid="dl-letter-btn"
                >
                  <span className="flex items-center gap-2"><FileText className="h-4 w-4" />Lettre de résiliation</span>
                  <Download className="h-4 w-4" />
                </button>
              )}
              {c.invoice_id && (
                <button
                  type="button"
                  onClick={() => downloadAuthed(`/invoices/${c.invoice_id}/pdf`, `facture-${c.id.slice(0, 8)}.pdf`)}
                  className="w-full flex items-center justify-between border border-border rounded-md p-2 hover:bg-secondary transition-colors duration-200 text-left"
                  data-testid="dl-invoice-btn"
                >
                  <span className="flex items-center gap-2"><FileText className="h-4 w-4" />Facture</span>
                  <Download className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => downloadAuthed(`/cases/${c.id}/evidence/zip`, `preuves-${c.id.slice(0, 8)}.zip`)}
                className="w-full flex items-center justify-between border border-border rounded-md p-2 hover:bg-secondary transition-colors duration-200 bg-secondary/40 text-left"
                data-testid="dl-evidence-zip-btn"
              >
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Archive complète (ZIP)</span>
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="card-elevated p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Justificatifs joints</div>
            <div className="text-sm space-y-1">
              {(c.documents || []).length === 0 && <div className="text-muted-foreground">Aucun justificatif</div>}
              {(c.documents || []).map((d) => (
                <div key={d.id} className="flex items-center gap-2"><FileText className="h-3 w-3 text-muted-foreground" />{d.name}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EvidencePage() {
  const [cases, setCases] = useState([]);
  useEffect(() => { api.get("/cases").then((r) => setCases(r.data.filter((c) => c.postal_shipment_id || c.letter_content))); }, []);
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Coffre-fort numérique</h1>
      <p className="text-muted-foreground mb-6">Toutes vos preuves archivées de manière sécurisée.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cases.map((c) => (
          <div key={c.id} className="card-elevated p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">{c.organization_name}</div>
              <CircleCheck className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-xs text-muted-foreground font-mono mb-3">{new Date(c.created_at).toLocaleDateString("fr-FR")} · {c.tracking_number || "—"}</div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => downloadAuthed(`/cases/${c.id}/evidence/zip`, `preuves-${c.id.slice(0, 8)}.zip`)}
              data-testid={`evidence-zip-${c.id}`}
            >
              <Download className="h-3 w-3 mr-1" />Télécharger l&apos;archive
            </Button>
          </div>
        ))}
        {cases.length === 0 && <div className="col-span-2 text-muted-foreground">Aucune preuve pour le moment.</div>}
      </div>
    </div>
  );
}

export function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/invoices").then((r) => setInvoices(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Factures</h1>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr><th className="text-left px-4 py-3">N°</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Montant</th><th className="text-left px-4 py-3">Statut</th><th></th></tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-border">
                <td className="px-4 py-3 font-mono">{i.number}</td>
                <td className="px-4 py-3">{new Date(i.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="px-4 py-3 font-mono">{fmtEur(i.amount_cents)}</td>
                <td className="px-4 py-3"><Badge variant="outline">{i.status}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => downloadAuthed(`/invoices/${i.id}/pdf`, `facture-${i.number}.pdf`)} data-testid={`dl-invoice-${i.id}`}>
                    <Download className="h-3 w-3 mr-1" />PDF
                  </Button>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Aucune facture.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ subject: "", message: "" });
  useEffect(() => { api.get("/support/tickets").then((r) => setTickets(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support/tickets", form);
      const r = await api.get("/support/tickets");
      setTickets(r.data);
      setForm({ subject: "", message: "" });
      toast.success("Ticket créé");
    } catch { toast.error("Erreur"); }
  };
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Support</h1>
      <form onSubmit={submit} className="card-elevated p-6 mb-6 space-y-4">
        <div><Label>Sujet</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="ticket-subject" /></div>
        <div><Label>Message</Label><textarea rows={4} className="w-full border border-border rounded-md p-3 bg-background text-sm" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="ticket-message" /></div>
        <Button type="submit" data-testid="ticket-submit">Envoyer</Button>
      </form>
      <div className="space-y-2">
        {tickets.map((t) => (
          <div key={t.id} className="card-elevated p-4">
            <div className="flex justify-between"><div className="font-semibold">{t.subject}</div><Badge variant="outline">{t.status}</Badge></div>
            <div className="text-sm text-muted-foreground mt-1">{t.message}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProfilePage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    postal_code: user?.postal_code || "",
    city: user?.city || "",
    country: user?.country || "France",
    address_check_status: user?.address_check_status || "ADDRESS_NOT_CHECKED",
  });
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const applyAddressPatch = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/auth/me", form);
      setUser(data);
      toast.success("Profil mis à jour");
    } catch { toast.error("Erreur"); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Mot de passe changé");
      setPw({ old_password: "", new_password: "" });
    } catch { toast.error("Ancien mot de passe incorrect"); }
  };
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Profil</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form onSubmit={save} className="card-elevated p-6 space-y-4">
          <h2 className="font-semibold">Informations personnelles</h2>
          <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
          <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <AddressAutocomplete
            value={{ address: form.address, postal_code: form.postal_code, city: form.city, country: form.country }}
            onChange={applyAddressPatch}
            testidPrefix="profile-address"
          />
          <Button type="submit" data-testid="profile-save-btn">Sauvegarder</Button>
        </form>
        <form onSubmit={changePw} className="card-elevated p-6 space-y-4">
          <h2 className="font-semibold">Sécurité</h2>
          <div><Label>Ancien mot de passe</Label><Input type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} required /></div>
          <div><Label>Nouveau mot de passe</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required minLength={8} /></div>
          <Button type="submit" variant="outline" data-testid="pw-change-btn">Changer le mot de passe</Button>
        </form>
      </div>
    </div>
  );
}
